import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import AuthImage from "@/components/AuthImage";
import { invalidateCategoriesCache } from "@/hooks/useCategories";
import {
  PlusCircle,
  PencilSimple,
  UploadSimple,
  Package,
  TrashSimple,
  ArrowUp,
  ArrowDown,
  ImageSquare,
} from "@phosphor-icons/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Preset colour themes — Tailwind class trios kept intact so JIT compiles them.
export const CATEGORY_THEMES = [
  { name: "Amber", blob: "bg-amber-100", fg: "text-amber-800", accent_fg: "text-amber-700" },
  { name: "Emerald", blob: "bg-emerald-100", fg: "text-emerald-800", accent_fg: "text-emerald-700" },
  { name: "Sky", blob: "bg-sky-100", fg: "text-sky-900", accent_fg: "text-sky-700" },
  { name: "Stone", blob: "bg-stone-100", fg: "text-stone-900", accent_fg: "text-stone-700" },
  { name: "Rose", blob: "bg-rose-100", fg: "text-rose-900", accent_fg: "text-rose-700" },
  { name: "Orange", blob: "bg-orange-100", fg: "text-orange-900", accent_fg: "text-orange-700" },
  { name: "Slate", blob: "bg-slate-100", fg: "text-slate-900", accent_fg: "text-slate-700" },
  { name: "Violet", blob: "bg-violet-100", fg: "text-violet-900", accent_fg: "text-violet-700" },
  { name: "Cyan", blob: "bg-cyan-100", fg: "text-cyan-900", accent_fg: "text-cyan-700" },
  { name: "Zinc", blob: "bg-zinc-100", fg: "text-zinc-900", accent_fg: "text-zinc-700" },
];

function themeKey(c) {
  return `${c.blob}|${c.fg}|${c.accent_fg}`;
}

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function AdminCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productCounts, setProductCounts] = useState({});
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, prodRes] = await Promise.all([
        api.get("/categories", { params: { include_inactive: true } }),
        api.get("/products", { params: { include_inactive: true } }),
      ]);
      setRows(catRes.data);
      const counts = {};
      for (const p of prodRes.data) {
        counts[p.category] = (counts[p.category] || 0) + 1;
      }
      setProductCounts(counts);
    } catch (e) {
      console.error("[AdminCategories] fetch failed", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const bust = () => {
    invalidateCategoriesCache();
    fetchAll();
  };

  const move = async (row, delta) => {
    const sorted = [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = sorted.findIndex((r) => r.category_id === row.category_id);
    const targetIdx = idx + delta;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    const other = sorted[targetIdx];
    try {
      await Promise.all([
        api.patch(`/categories/${row.category_id}`, { order: other.order }),
        api.patch(`/categories/${other.category_id}`, { order: row.order }),
      ]);
      bust();
    } catch (e) {
      toast.error("Failed to reorder");
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/categories/${row.category_id}`, { is_active: !row.is_active });
      toast.success(row.is_active ? "Category hidden" : "Category shown");
      bust();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const hardDelete = async (row) => {
    const count = productCounts[row.category_id] || 0;
    if (count > 0) {
      toast.error(`Cannot delete — ${count} product(s) still use this category. Reassign or delete those first.`);
      return;
    }
    if (!window.confirm(`Permanently delete "${row.label}"? This can't be undone.`)) return;
    try {
      await api.delete(`/categories/${row.category_id}`, { params: { hard: true } });
      toast.success("Category deleted");
      bust();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed";
      toast.error(typeof detail === "string" ? detail : "Failed");
    }
  };

  const removeIcon = async (row) => {
    if (!window.confirm("Remove the uploaded icon for this category?")) return;
    try {
      await api.delete(`/categories/${row.category_id}/icon`);
      toast.success("Icon removed");
      bust();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Factory Console · Categories</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-zinc-900 mt-2">
          Category management
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-2xl">
          Add, rename or hide the categories shown on the client home. Upload a 512×512 icon for each — it replaces the placeholder image on the tile.
        </p>
      </div>

      <div className="flex justify-end mb-6">
        <button
          onClick={() => setCreating(true)}
          data-testid="admin-new-category-button"
          className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-5 py-3 font-medium transition-colors"
        >
          <PlusCircle size={18} weight="fill" /> New Category
        </button>
      </div>

      <div className="bg-white border border-zinc-200" data-testid="admin-categories-table">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-200">
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt w-20">Order</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt w-24">Icon</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Label</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">ID</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Theme</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Products</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Status</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              ["s-1", "s-2", "s-3"].map((k) => (
                <TableRow key={k}>
                  <TableCell colSpan={8}>
                    <div className="h-14 bg-zinc-100 animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-14 text-zinc-500">
                  <Package size={40} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
                  No categories yet — add your first.
                </TableCell>
              </TableRow>
            ) : (
              rows
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((c, i, arr) => (
                  <TableRow
                    key={c.category_id}
                    className="hover:bg-zinc-50 border-zinc-200"
                    data-testid={`category-row-${c.category_id}`}
                  >
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <div className="font-mono-mt text-sm text-zinc-500">{c.order ?? 0}</div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => move(c, -1)}
                            disabled={i === 0}
                            data-testid={`category-up-${c.category_id}`}
                            className="p-1 border border-zinc-200 hover:border-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowUp size={12} />
                          </button>
                          <button
                            onClick={() => move(c, 1)}
                            disabled={i === arr.length - 1}
                            data-testid={`category-down-${c.category_id}`}
                            className="p-1 border border-zinc-200 hover:border-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowDown size={12} />
                          </button>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={`w-14 h-14 ${c.blob} grid place-items-center overflow-hidden border border-zinc-200`}>
                        {c.icon_path ? (
                          <AuthImage
                            path={c.icon_path}
                            alt={c.label}
                            className="w-full h-full object-contain"
                            fallback={<Package size={22} className={c.accent_fg} weight="duotone" />}
                          />
                        ) : c.image_url ? (
                          <img src={c.image_url} alt={c.label} className="w-full h-full object-cover" />
                        ) : (
                          <Package size={22} className={c.accent_fg} weight="duotone" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-zinc-900">{c.label}</div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono-mt text-xs text-zinc-500">{c.category_id}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`w-4 h-4 ${c.blob} border border-zinc-200`} />
                        <span className="font-mono-mt text-xs text-zinc-500 truncate max-w-[120px]">{c.blob}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono-mt text-sm text-zinc-600">
                      {productCounts[c.category_id] || 0}
                    </TableCell>
                    <TableCell>
                      {c.is_active ? (
                        <span className="text-xs font-semibold text-green-700">ACTIVE</span>
                      ) : (
                        <span className="text-xs font-semibold text-zinc-400">HIDDEN</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditing(c)}
                          data-testid={`edit-category-${c.category_id}`}
                          className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium transition-colors"
                        >
                          <PencilSimple size={14} /> Edit
                        </button>
                        <button
                          onClick={() => toggleActive(c)}
                          data-testid={`toggle-category-${c.category_id}`}
                          className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-amber-500 hover:text-amber-600 px-3 py-1.5 text-xs font-medium transition-colors"
                        >
                          {c.is_active ? "Hide" : "Show"}
                        </button>
                        <button
                          onClick={() => hardDelete(c)}
                          data-testid={`delete-category-${c.category_id}`}
                          title="Delete permanently"
                          className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-red-500 hover:text-red-500 px-3 py-1.5 text-xs font-medium transition-colors"
                        >
                          <TrashSimple size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </div>

      <NewCategoryDialog
        open={creating}
        onOpenChange={setCreating}
        existingIds={rows.map((r) => r.category_id)}
        nextOrder={(rows.reduce((m, r) => Math.max(m, r.order ?? 0), 0) || 0) + 1}
        onCreated={bust}
      />
      <EditCategoryDialog
        category={editing}
        onClose={() => setEditing(null)}
        onSaved={bust}
        onIconRemoved={removeIcon}
      />
    </Layout>
  );
}

function ThemeSelect({ value, onChange, testid }) {
  const key = themeKey(value);
  return (
    <Select
      value={key}
      onValueChange={(k) => {
        const t = CATEGORY_THEMES.find((x) => themeKey(x) === k);
        if (t) onChange({ blob: t.blob, fg: t.fg, accent_fg: t.accent_fg });
      }}
    >
      <SelectTrigger data-testid={testid} className="h-11 rounded-none border-zinc-200">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-none">
        {CATEGORY_THEMES.map((t) => (
          <SelectItem key={themeKey(t)} value={themeKey(t)}>
            <span className="inline-flex items-center gap-2">
              <span className={`w-3 h-3 ${t.blob} border border-zinc-200`} />
              {t.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function NewCategoryDialog({ open, onOpenChange, existingIds, nextOrder, onCreated }) {
  const [label, setLabel] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [theme, setTheme] = useState(CATEGORY_THEMES[0]);
  const [order, setOrder] = useState(nextOrder);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setLabel("");
      setCategoryId("");
      setIdTouched(false);
      setTheme(CATEGORY_THEMES[0]);
      setOrder(nextOrder);
    }
  }, [open, nextOrder]);

  const onLabelChange = (v) => {
    setLabel(v);
    if (!idTouched) setCategoryId(slugify(v));
  };

  const submit = async () => {
    const id = slugify(categoryId || label);
    if (!label.trim()) return toast.error("Label is required");
    if (!id) return toast.error("Slug is required");
    if (existingIds.includes(id)) return toast.error("A category with this ID already exists");
    setBusy(true);
    try {
      await api.post("/categories", {
        category_id: id,
        label: label.trim(),
        blob: theme.blob,
        fg: theme.fg,
        accent_fg: theme.accent_fg,
        order: Number(order) || 999,
      });
      toast.success("Category created");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-zinc-200 max-w-lg" data-testid="new-category-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add new category</DialogTitle>
          <DialogDescription>
            Give it a name and colour theme. You can upload the icon after it's created.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Label *</div>
            <Input
              data-testid="new-category-label-input"
              value={label}
              onChange={(e) => onLabelChange(e.target.value)}
              placeholder="e.g. Marble"
              className="h-11 rounded-none border-zinc-200"
            />
          </div>
          <div>
            <div className="mt-tiny text-zinc-500 mb-1.5">Slug (URL id) *</div>
            <Input
              data-testid="new-category-id-input"
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setIdTouched(true);
              }}
              placeholder="kebab-case"
              className="h-11 rounded-none border-zinc-200 font-mono-mt"
            />
            <div className="mt-tiny text-zinc-400 mt-1">Lowercase letters, digits, hyphens. Auto-generated from the label.</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Colour theme</div>
              <ThemeSelect value={theme} onChange={setTheme} testid="new-category-theme-select" />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Sort order</div>
              <Input
                data-testid="new-category-order-input"
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="h-11 rounded-none border-zinc-200"
              />
            </div>
          </div>

          <div className={`p-4 ${theme.blob} border border-zinc-200 flex items-center gap-3`}>
            <div className="w-14 h-14 bg-white border border-zinc-200 grid place-items-center">
              <ImageSquare size={22} className={theme.accent_fg} weight="duotone" />
            </div>
            <div>
              <div className="font-display font-bold text-zinc-900">{label || "Preview"}</div>
              <div className="mt-tiny text-zinc-500">Upload an icon after creating.</div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="new-category-submit-button"
            className="bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60"
          >
            {busy ? "Creating..." : "Create category"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCategoryDialog({ category, onClose, onSaved, onIconRemoved }) {
  const [label, setLabel] = useState("");
  const [theme, setTheme] = useState(CATEGORY_THEMES[0]);
  const [order, setOrder] = useState(0);
  const [imageUrl, setImageUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (category) {
      setLabel(category.label || "");
      setTheme(
        CATEGORY_THEMES.find((t) => themeKey(t) === themeKey(category)) || {
          blob: category.blob,
          fg: category.fg,
          accent_fg: category.accent_fg,
          name: "Custom",
        }
      );
      setOrder(category.order ?? 0);
      setImageUrl(category.image_url || "");
      setIsActive(!!category.is_active);
      setFile(null);
      setPreview(null);
    }
  }, [category]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const saveFields = async () => {
    if (!category) return;
    if (!label.trim()) return toast.error("Label is required");
    setBusy(true);
    try {
      await api.patch(`/categories/${category.category_id}`, {
        label: label.trim(),
        blob: theme.blob,
        fg: theme.fg,
        accent_fg: theme.accent_fg,
        order: Number(order) || 0,
        image_url: imageUrl || null,
        is_active: isActive,
      });
      if (file) {
        const form = new FormData();
        form.append("image", file);
        await api.post(`/categories/${category.category_id}/icon`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Category updated");
      if (inputRef.current) inputRef.current.value = "";
      onClose();
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
    }
    setBusy(false);
  };

  const uploadIconOnly = async () => {
    if (!category || !file) return;
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("image", file);
      await api.post(`/categories/${category.category_id}/icon`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Icon uploaded");
      setFile(null);
      setPreview(null);
      if (inputRef.current) inputRef.current.value = "";
      onSaved?.();
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploadBusy(false);
  };

  return (
    <Dialog open={!!category} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-none border-zinc-200 max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="edit-category-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit category</DialogTitle>
          <DialogDescription>
            Icon should be a square PNG or JPG, 512×512 recommended. Transparent PNGs look best on the coloured tile.
          </DialogDescription>
        </DialogHeader>

        {category && (
          <div className="space-y-4">
            {/* Icon upload */}
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Icon (512×512 recommended)</div>
              <div className="flex items-center gap-4">
                <div className={`w-24 h-24 ${theme.blob} grid place-items-center border border-zinc-200 overflow-hidden shrink-0`}>
                  {preview ? (
                    <img src={preview} alt="preview" className="w-full h-full object-contain" />
                  ) : category.icon_path ? (
                    <AuthImage
                      path={category.icon_path}
                      alt={category.label}
                      className="w-full h-full object-contain"
                      fallback={<Package size={30} className={theme.accent_fg} weight="duotone" />}
                    />
                  ) : category.image_url ? (
                    <img src={category.image_url} alt={category.label} className="w-full h-full object-cover" />
                  ) : (
                    <Package size={30} className={theme.accent_fg} weight="duotone" />
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label
                    data-testid="edit-category-icon-label"
                    className="border border-dashed border-zinc-300 hover:border-[#0047AB] hover:bg-zinc-50 px-3 py-2.5 flex items-center justify-center gap-2 cursor-pointer transition-colors text-sm"
                  >
                    <UploadSimple size={16} className="text-[#0047AB]" />
                    {file ? file.name : "Choose icon file (PNG/JPG)"}
                    <input
                      ref={inputRef}
                      type="file"
                      accept="image/*"
                      onChange={pickFile}
                      className="hidden"
                      data-testid="edit-category-icon-input"
                    />
                  </label>
                  <div className="flex gap-2">
                    {file && (
                      <button
                        onClick={uploadIconOnly}
                        disabled={uploadBusy}
                        data-testid="edit-category-icon-upload-button"
                        className="text-xs border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 disabled:opacity-50"
                      >
                        {uploadBusy ? "Uploading..." : "Upload only"}
                      </button>
                    )}
                    {category.icon_path && (
                      <button
                        onClick={() => onIconRemoved?.(category)}
                        data-testid="edit-category-icon-remove-button"
                        className="text-xs border border-zinc-200 hover:border-red-500 hover:text-red-500 px-3 py-1.5"
                      >
                        Remove icon
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Label *</div>
              <Input
                data-testid="edit-category-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="h-11 rounded-none border-zinc-200"
              />
            </div>

            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Slug (URL id)</div>
              <Input value={category.category_id} disabled className="h-11 rounded-none border-zinc-200 bg-zinc-50 font-mono-mt" />
              <div className="mt-tiny text-zinc-400 mt-1">Slug cannot be changed after creation.</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Colour theme</div>
                <ThemeSelect value={theme} onChange={setTheme} testid="edit-category-theme-select" />
              </div>
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Sort order</div>
                <Input
                  data-testid="edit-category-order-input"
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  className="h-11 rounded-none border-zinc-200"
                />
              </div>
            </div>

            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">External image URL (optional fallback)</div>
              <Input
                data-testid="edit-category-image-url-input"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                className="h-11 rounded-none border-zinc-200"
              />
              <div className="mt-tiny text-zinc-400 mt-1">Shown when no icon is uploaded. Leave blank to fall back to a generic icon.</div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                data-testid="edit-category-active-checkbox"
                className="w-4 h-4"
              />
              <span className="text-sm text-zinc-700">Show this category to clients</span>
            </label>
          </div>
        )}

        <DialogFooter>
          <button
            onClick={onClose}
            className="border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={saveFields}
            disabled={busy}
            data-testid="edit-category-submit-button"
            className="bg-[#0047AB] hover:bg-[#003380] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60"
          >
            {busy ? "Saving..." : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
